import { useEffect, useState } from 'react';

import { Badge, Button, Icon } from '@ds/core';
import { Timeline } from '@ds/data';
import type { TimelineEvent } from '@ds/data';
import { Alert, EmptyState, Modal } from '@ds/feedback';
import { dataEHoraPorExtenso } from '@/comuns/formato';
import { consultasDoProntuario, trechoDoResumo } from '@/data/linhaDoTempoDoProntuario';
import type { ConsultaDoProntuario } from '@/data/linhaDoTempoDoProntuario';
import type { ResumoDaConsulta } from '@/data/notaDeIa';
import { dados } from '@/data/porta';
import type { FichaPreenchida, ProntuarioDoPaciente } from '@/data/tipos';
import { impersonacaoAtual } from '@/session/sessao';
import { FichaLida } from './FichaClinica';

export interface LinhaDoTempoDoProntuarioProps {
    contatoId: string;
    prontuario: ProntuarioDoPaciente;
    onImprimir: (fichaId: string) => void;
    /** Ausente sem permissão de corrigir. */
    onAlterar?: (ficha: FichaPreenchida) => void;
}

/**
 * A aba Prontuário como linha do tempo de consultas (pedido de 16/09/2026).
 *
 * Cada consulta com registro vira uma linha; clicar abre o resumo aprovado da
 * VecchIA e as fichas daquela consulta. Quem a clínica não deixa ler o resumo
 * (0156) vê que ele existe, não o texto.
 */
export function LinhaDoTempoDoProntuario({ contatoId, prontuario, onImprimir, onAlterar }: LinhaDoTempoDoProntuarioProps) {
    const [resumos, setResumos] = useState<ResumoDaConsulta[]>([]);
    const [erro, setErro] = useState<string | null>(null);
    const [aberta, setAberta] = useState<string | null>(null);

    useEffect(() => {
        /* Impersonação não é membro: o banco recusa, e o suporte não lê resumo clínico. */
        if (impersonacaoAtual()) return;
        let vivo = true;
        dados().resumosDaIaDoPaciente(contatoId)
            .then((r) => { if (vivo) { setResumos(r); setErro(null); } })
            .catch((e: unknown) => { if (vivo) setErro(e instanceof Error ? e.message : 'Não foi possível ler os resumos da VecchIA.'); });
        return () => { vivo = false; };
    }, [contatoId, prontuario]);

    const consultas = consultasDoProntuario(prontuario, resumos);
    const consultaAberta = consultas.find((c) => c.atendimentoId === aberta);

    const eventos: TimelineEvent[] = consultas.map((c) => {
        const resumo = c.resumos.find((r) => r.visivel && r.resumo);
        const temResumo = c.resumos.length > 0;
        return {
            id: c.atendimentoId,
            icon: temResumo ? 'sparkles' : 'clipboard-pen',
            tone: temResumo ? 'brand' : 'neutral',
            title: (
                <button
                    type="button"
                    onClick={() => setAberta(c.atendimentoId)}
                    style={{ padding: 0, border: 0, background: 'none', cursor: 'pointer', font: 'inherit', fontWeight: 600, color: 'var(--text-brand)', textAlign: 'left', textDecoration: 'underline', textUnderlineOffset: 3 }}
                >
                    {c.rotulo}
                </button>
            ),
            description: (
                <span style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {resumo?.resumo && <span>{trechoDoResumo(resumo.resumo)}</span>}
                    {temResumo && !resumo && <span>Há um resumo da VecchIA. A clínica restringiu quem pode lê-lo.</span>}
                    <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {temResumo && <Badge tone="brand">Resumo da VecchIA</Badge>}
                        {c.fichas.length > 0 && (
                            <Badge tone="neutral">{c.fichas.length === 1 ? c.fichas[0]?.nome : `${c.fichas.length} fichas`}</Badge>
                        )}
                    </span>
                </span>
            ),
        };
    });

    return (
        <>
            {erro && <Alert tone="warning" dismissible={false} style={{ marginBottom: 12 }}>{erro}</Alert>}
            {consultas.length === 0 ? (
                <EmptyState compact icon="clipboard-pen" title="Nenhum registro ainda" description="Fichas e resumos aprovados das consultas aparecem aqui." />
            ) : (
                <Timeline events={eventos} />
            )}

            {consultaAberta && (
                <ConsultaDoProntuarioModal
                    consulta={consultaAberta}
                    onFechar={() => setAberta(null)}
                    onImprimir={(id) => { setAberta(null); onImprimir(id); }}
                    {...(onAlterar ? { onAlterar: (f: FichaPreenchida) => { setAberta(null); onAlterar(f); } } : {})}
                />
            )}
        </>
    );
}

interface ConsultaDoProntuarioModalProps {
    consulta: ConsultaDoProntuario;
    onFechar: () => void;
    onImprimir: (fichaId: string) => void;
    onAlterar?: (ficha: FichaPreenchida) => void;
}

function ConsultaDoProntuarioModal({ consulta, onFechar, onImprimir, onAlterar }: ConsultaDoProntuarioModalProps) {
    return (
        <Modal
            open width={720} onClose={onFechar}
            title={`Consulta de ${consulta.rotulo}`}
            footer={<Button variant="secondary" onClick={onFechar}>Fechar</Button>}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {consulta.resumos.map((r) => (
                    <section
                        key={r.notaId}
                        aria-label="Resumo da VecchIA"
                        style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 14, border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', background: 'var(--surface)' }}
                    >
                        <p style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 6, fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)' }}>
                            <Icon name="sparkles" size={14} style={{ color: 'var(--text-brand)' }} />
                            Resumo da VecchIA
                        </p>
                        <p style={{ margin: 0, fontSize: 12, color: 'var(--text-tertiary)' }}>
                            {r.modo === 'consulta' ? 'Consulta gravada' : 'Ditado'}
                            {` · aprovado em ${dataEHoraPorExtenso(r.aprovadoEm)}`}
                            {r.autor ? ` por ${r.autor}` : ''}
                        </p>
                        {r.visivel && r.resumo ? (
                            <p style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 13.5, lineHeight: 1.6, color: 'var(--text-body)' }}>{r.resumo}</p>
                        ) : (
                            <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>A clínica restringiu quem pode ler este resumo.</p>
                        )}
                    </section>
                ))}

                {consulta.fichas.map((f) => (
                    <FichaLida
                        key={f.id} ficha={f}
                        onImprimir={() => onImprimir(f.id)}
                        {...(onAlterar && f.definicaoUsada ? { onAlterar: () => onAlterar(f) } : {})}
                    />
                ))}
                {consulta.fichas.length === 0 && (
                    <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>Nenhuma ficha preenchida nesta consulta.</p>
                )}
            </div>
        </Modal>
    );
}
